import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { InfoAlert } from '../components/InfoAlert';
import { SuccessAlert } from '../components/SuccessAlert';
import { MapPin, Car, Building2, Search, Calendar, Hash } from 'lucide-react';

export const FiltersGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Filters System'
      description='Master the powerful filtering system to find the right recalls quickly'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Filters System' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Hero Section */}
        <div className='mb-20'>
          <InfoAlert title='What Are Filters?'>
            <p className='text-sm leading-6'>
              Filters are your most powerful tool for finding the right recalls quickly. They help
              you narrow down thousands of recalls to exactly what you need - whether that is
              recalls in a specific area, for a specific campaign, or matching a technician's
              skills.
            </p>
          </InfoAlert>
        </div>

        {/* Where Filters Appear */}
        <section className='mb-20' id='where-filters-appear'>
          <h2 className='text-3xl font-bold mb-6'>Where Filters Appear</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Filters are available throughout Vehix in these key locations:
          </p>
          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Recall Map</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Top of the page to filter which recalls appear on the map
                </p>
              </CardContent>
            </Card>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Recall List</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Top of the page to narrow down the searchable list
                </p>
              </CardContent>
            </Card>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Manual Routing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Sidebar filters to select recalls for assignment
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Filter Categories */}
        <section className='mb-20' id='filter-categories'>
          <h2 className='text-3xl font-bold mb-6'>Filter Categories</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            All filters are organized into collapsible sections for easy navigation. Click a section
            header to expand or collapse it.
          </p>

          <div className='space-y-12'>
            {/* Vehicle Details */}
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-8'>
                <div className='flex items-center gap-4 mb-4'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Car className='h-8 w-8 text-primary' />
                  </div>
                  <div>
                    <CardTitle className='text-lg font-bold mb-2'>
                      Vehicle Details Filters
                    </CardTitle>
                    <CardDescription className='text-base'>
                      Find specific vehicles or vehicle types
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='space-y-10'>
                {/* VIN Search */}
                <div>
                  <div className='flex items-center gap-3 mb-4'>
                    <Search className='h-6 w-6 text-primary' />
                    <h3 className='text-lg font-semibold'>VIN Search</h3>
                  </div>
                  <p className='text-sm leading-6 text-muted-foreground mb-6'>
                    Finds exact or partial VIN matches
                  </p>
                  <div className='bg-muted/50 p-6 rounded-lg space-y-4'>
                    <div className='flex items-center gap-4'>
                      <Badge variant='secondary' className='text-sm font-medium px-3 py-1'>
                        Full VIN
                      </Badge>
                      <code className='text-sm font-mono'>1HGBH41JXMN109186</code>
                    </div>
                    <div className='flex items-center gap-4'>
                      <Badge variant='secondary' className='text-sm font-medium px-3 py-1'>
                        Last 6
                      </Badge>
                      <code className='text-sm font-mono'>109186</code>
                    </div>
                    <div className='flex items-center gap-4'>
                      <Badge variant='secondary' className='text-sm font-medium px-3 py-1'>
                        First 8
                      </Badge>
                      <code className='text-sm font-mono'>1HGBH41J</code>
                    </div>
                  </div>
                  <p className='text-sm text-muted-foreground mt-4 italic'>
                    💡 Pro tip: Last 6 digits usually sufficient and easier to type
                  </p>
                </div>

                {/* Make Dropdown */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>Make Dropdown</h3>
                  <p className='text-sm leading-6 text-muted-foreground mb-6'>
                    Filters to specific manufacturer
                  </p>
                  <div className='flex flex-wrap gap-3 mb-4'>
                    <Badge variant='outline' className='text-sm font-medium px-4 py-2'>
                      Honda
                    </Badge>
                    <Badge variant='outline' className='text-sm font-medium px-4 py-2'>
                      Toyota
                    </Badge>
                    <Badge variant='outline' className='text-sm font-medium px-4 py-2'>
                      Ford
                    </Badge>
                    <Badge variant='outline' className='text-sm font-medium px-4 py-2'>
                      Chevrolet
                    </Badge>
                  </div>
                  <ul className='space-y-2 text-base text-muted-foreground'>
                    <li>📝 Searchable: Type to quickly find</li>
                    <li>🔗 Cascading: Selecting Make filters Model dropdown</li>
                  </ul>
                </div>

                {/* Model Dropdown */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>Model Dropdown</h3>
                  <p className='text-sm leading-6 text-muted-foreground mb-4'>
                    Filters to specific vehicle model
                  </p>
                  <ul className='space-y-2 text-base text-muted-foreground'>
                    <li>⚙️ Dynamic: Only shows models for selected Make</li>
                    <li>📝 Searchable: Type to quickly find</li>
                  </ul>
                </div>

                {/* Year Input */}
                <div>
                  <div className='flex items-center gap-3 mb-4'>
                    <Calendar className='h-6 w-6 text-primary' />
                    <h3 className='text-lg font-semibold'>Year Input</h3>
                  </div>
                  <p className='text-sm leading-6 text-muted-foreground mb-4'>
                    Filters to specific model year
                  </p>
                  <div className='bg-muted/50 p-4 rounded-lg'>
                    <p className='text-base'>Accepts: 4-digit year (e.g., 2020)</p>
                  </div>
                  <p className='text-sm text-muted-foreground mt-4'>
                    🎯 Use case: Prioritize newer vehicles for customer retention
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Location Filters */}
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-8'>
                <div className='flex items-center gap-4 mb-4'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <MapPin className='h-8 w-8 text-primary' />
                  </div>
                  <div>
                    <CardTitle className='text-lg font-bold mb-2'>Location Filters</CardTitle>
                    <CardDescription className='text-base'>
                      Find recalls in specific geographic areas
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='space-y-10'>
                {/* Dealer Dropdown */}
                <div>
                  <div className='flex items-center gap-3 mb-4'>
                    <Building2 className='h-6 w-6 text-primary' />
                    <h3 className='text-lg font-semibold'>Dealer Dropdown</h3>
                  </div>
                  <p className='text-sm leading-6 text-muted-foreground mb-4'>
                    Filters to specific dealership
                  </p>
                  <div className='bg-muted/50 p-6 rounded-lg space-y-3'>
                    <p className='text-base'>📝 Searchable: Type dealer name</p>
                    <p className='text-base'>🎯 Use case: Focus on specific dealer's recalls</p>
                    <p className='text-base font-semibold'>
                      Example: "Victory Honda" shows only that dealer's work
                    </p>
                  </div>
                </div>

                {/* City Search */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>City Search</h3>
                  <p className='text-sm leading-6 text-muted-foreground mb-4'>
                    Finds all dealers in a city
                  </p>
                  <div className='bg-muted/50 p-6 rounded-lg space-y-3'>
                    <p className='text-base'>🔍 Type-ahead: Starts suggesting after 2 characters</p>
                    <p className='text-base'>🎯 Use case: Plan city-wide mobile routes</p>
                    <p className='text-base font-semibold'>
                      Example: Type "Miami" → See all Miami dealers
                    </p>
                  </div>
                </div>

                {/* ZIP Code Search */}
                <div>
                  <div className='flex items-center gap-3 mb-4'>
                    <Hash className='h-6 w-6 text-primary' />
                    <h3 className='text-lg font-semibold'>ZIP Code Search</h3>
                  </div>
                  <p className='text-sm leading-6 text-muted-foreground mb-4'>
                    Finds dealers by postal code
                  </p>
                  <ul className='space-y-2 text-base text-muted-foreground'>
                    <li>🔍 Type-ahead: Starts suggesting after 3 digits</li>
                    <li>🌐 Powerful radius: Combines with 2-3 digit range</li>
                    <li>🎯 Use case: Mobile service planning</li>
                  </ul>
                </div>

                {/* Radius Selector */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>Radius Selector</h3>
                  <p className='text-sm leading-6 text-muted-foreground mb-6'>
                    Expands ZIP search to surrounding area
                  </p>
                  <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
                    {[
                      { label: 'Exact', desc: 'Only this ZIP' },
                      { label: '5 miles', desc: 'Very local' },
                      { label: '10 miles', desc: 'Local area' },
                      { label: '15 miles', desc: 'Broader area' }
                    ].map(item => (
                      <div key={item.label} className='bg-muted/50 p-4 rounded-lg text-center'>
                        <Badge variant='outline' className='mb-3 text-sm font-medium px-3 py-1'>
                          {item.label}
                        </Badge>
                        <p className='text-sm text-muted-foreground'>{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pro Tips */}
        <section className='mb-20' id='filter-pro-tips'>
          <SuccessAlert title='Filter Pro Tips'>
            <ul className='space-y-4 text-sm leading-6'>
              <li>
                <strong>Combine filters</strong> for precision: Make + Model + City = perfect
                routing sets
              </li>
              <li>
                <strong>Last 6 VIN digits</strong> are fastest for customer calls (they can read
                from registration)
              </li>
              <li>
                <strong>Start broad, then narrow</strong>: City first, then refine by campaign or
                year
              </li>
              <li>
                <strong>Clear filters often</strong> to avoid accidentally hiding recalls you need
                to see
              </li>
            </ul>
          </SuccessAlert>
        </section>

        {/* Common Workflows */}
        <section className='mb-20' id='common-filter-workflows'>
          <h2 className='text-3xl font-bold mb-6'>Common Filter Workflows</h2>
          <div className='grid gap-8 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-6'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0'>
                    1
                  </div>
                  <CardTitle className='text-lg font-semibold leading-tight'>
                    Customer Call About Specific Vehicle
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-5'>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 1</Badge>
                  <p className='text-sm leading-6'>Open Recall List</p>
                </div>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 2</Badge>
                  <p className='text-sm leading-6'>
                    Use VIN Search filter with last 6 digits customer provides
                  </p>
                </div>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 3</Badge>
                  <p className='text-sm leading-6'>Instantly see all recalls for that vehicle</p>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-6'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0'>
                    2
                  </div>
                  <CardTitle className='text-lg font-semibold leading-tight'>
                    Planning Mobile Routes for a City
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-5'>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 1</Badge>
                  <p className='text-sm leading-6'>Open Recall Map</p>
                </div>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 2</Badge>
                  <p className='text-sm leading-6'>Use City Search filter (e.g., "Miami")</p>
                </div>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 3</Badge>
                  <p className='text-sm leading-6'>
                    Optionally add Make/Model to focus on what your techs specialize in
                  </p>
                </div>
                <div className='flex gap-4'>
                  <Badge className='flex-shrink-0 h-fit'>Step 4</Badge>
                  <p className='text-sm leading-6'>
                    See all matching recalls geographically - perfect for efficient routing
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Next Steps */}
        <section id='next-steps'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Now that you understand filters, explore these related features:
          </p>
          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            <Link to='/docs/features/recall-map' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Recall Map
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Use filters to visualize specific recalls on the map
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to='/docs/features/recall-list' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Recall List
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Combine filters with search for powerful recall discovery
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to='/docs/features/manual-routing' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Manual Routing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Use filters to select recalls for route assignment
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
